// Helpers for displaying tag hierarchy (parent → child paths) in pickers and
// facets. Tags form a multi-parent DAG (`tag.parents` is an array of ids), so a
// tag has no single canonical path — for DISPLAY we pick one deterministic path
// (the parent whose name sorts first) and signal "also under others" separately.

/** Build an id → tag Map from a flat tag list. */
export function indexTagsById(tags) {
    const m = new Map();
    for (const t of tags || []) if (t && t.id != null) m.set(t.id, t);
    return m;
}

/** Convert a plain { id: tag } object (e.g. an Algolia tagMap) into a Map. */
export function tagMapToIndex(tagMap) {
    return new Map(Object.entries(tagMap || {}));
}

/** Number of a tag's parents that resolve to real tags in the index. */
export function validParentCount(tag, byId) {
    if (!tag || !Array.isArray(tag.parents)) return 0;
    return tag.parents.filter((p) => byId.has(p)).length;
}

/** True when the tag sits under more than one parent (a real DAG node). */
export function hasMultipleParents(tag, byId) {
    return validParentCount(tag, byId) > 1;
}

/**
 * Deterministic ancestor chain ROOT → … → immediate parent (EXCLUDING the tag
 * itself). For a multi-parent tag we follow the parent whose name sorts first so
 * the breadcrumb is stable. Cycle-guarded — returns whatever it resolved before
 * hitting a repeat.
 */
export function getAncestorPath(tagId, byId, _visited) {
    const visited = _visited || new Set();
    const tag = byId.get(tagId);
    if (!tag || visited.has(tagId)) return [];
    visited.add(tagId);

    const parents = (tag.parents || []).map((p) => byId.get(p)).filter(Boolean);
    if (parents.length === 0) return [];
    parents.sort((a, b) => a.name.localeCompare(b.name) || String(a.id).localeCompare(String(b.id)));
    const chosen = parents[0];
    return [...getAncestorPath(chosen.id, byId, visited), chosen];
}

/** "Music › Action" — the ancestors only ("" for a top-level tag). */
export function getAncestorPathLabel(tagId, byId, sep = ' › ') {
    return getAncestorPath(tagId, byId).map((t) => t.name).join(sep);
}

/** "Music › Action › Battle" — the full path including the tag itself (tooltips). */
export function getFullPathLabel(tagId, byId, sep = ' › ') {
    const tag = byId.get(tagId);
    const names = getAncestorPath(tagId, byId).map((t) => t.name);
    names.push(tag ? tag.name : String(tagId));
    return names.join(sep);
}
